import { createContext, useReducer, useMemo, useCallback } from "react";
import type { FC, ReactNode } from "react";
import useAsyncEffect from "use-async-effect";
import type { User } from "../types/user";
import { userApi } from "../api/userApi";
import { Organization } from "../types/organization";
import { auth0Client } from "./auth0Client";

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
  organizations: Organization[] | [];
}

export interface AuthContextValue extends State {
  platform: "Auth0";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loginWithPopup: (options?: any) => Promise<void>;
  logout: () => void;
  clearCredentials: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: "INITIALIZE";
  payload: {
    isAuthenticated: boolean;
    user: User | null;
    organizations?: Organization[];
  };
};

type LoginAction = {
  type: "LOGIN";
  payload: {
    user: User;
    organizations?: Organization[];
  };
};

type LogoutAction = {
  type: "LOGOUT";
};

type RegisterAction = {
  type: "REGISTER";
};

type Action = InitializeAction | LoginAction | LogoutAction | RegisterAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  organizations: [],
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user, organizations } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      organizations: organizations ?? [],
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user, organizations } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      organizations,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: "Auth0",
  loginWithPopup: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  clearCredentials: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useAsyncEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        await auth0Client.checkSession();

        const isAuthenticated = await auth0Client.isAuthenticated();

        if (isAuthenticated) {
          const user = await auth0Client.getUser();

          // if organizations exist
          const userOrganizations = await userApi.getOrganazitionsForUser(
            user.sub.split("|")[1],
          );

          // Here you should extract the complete user profile to make it
          // available in your entire app.
          // The auth state only provides basic information.
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated,
              user: {
                id: user.sub,
                avatar: user.picture,
                email: user.email,
                name: `${user.given_name} ${user.family_name}`,
                plan: "Premium",
              },
              organizations: userOrganizations || [],
            },
          });
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated,
              user: null,
              organizations: [],
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
            organizations: [],
          },
        });
      }
    };

    initialize();
  }, []);
  const loginWithPopup = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (options?: any): Promise<void> => {
      if (auth0Client) {
        await auth0Client.loginWithPopup(options);

        const isAuthenticated = await auth0Client.isAuthenticated();

        if (isAuthenticated) {
          const user = await auth0Client.getUser();
          if (user) {
            const userOrganizations = await userApi.getOrganazitionsForUser(
              user.sub.split("|")[1],
            );

            dispatch({
              type: "LOGIN",
              payload: {
                user: {
                  id: user.sub,
                  avatar: user.picture,
                  email: user.email,
                  name: `${user.given_name} ${user.family_name}`,
                  plan: "Premium",
                },
                organizations: userOrganizations || [],
              },
            });
          }
        }
      }
    },
    [dispatch],
  );

  const logout = useCallback((): void => {
    if (auth0Client) {
      auth0Client.logout();
      dispatch({
        type: "LOGOUT",
      });
    }
  }, [dispatch]);

  const clearCredentials = useCallback((): void => {
    dispatch({
      type: "INITIALIZE",
      payload: {
        isAuthenticated: false,
        user: null,
        organizations: [],
      },
    });
  }, [dispatch]);

  const contextValue = useMemo(
    () => ({
      ...state,
      platform: "Auth0" as const,
      loginWithPopup,
      logout,
      clearCredentials,
    }),
    [state, loginWithPopup, logout, clearCredentials],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
